<template>
    <transition name="fade">
        <div
            class="share-container"
            @touchstart="dragstart"
            @touchend="dragend"
            @touchmove="dragmove"
        >
            <transition name="fade" mode="in-out">
                <div v-show="shareLoading" class="share-loading">
                    <div class="loading-indicator">
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                    <p>We're putting your outfit together. You'll love it.</p>
                    <img :src="shareImageUrl" />
                    <component v-if="currentIcon" :is="currentIcon"></component>
                </div>
            </transition>
            <transition name="fade" mode="in-out">
                <div v-show="!shareLoading">
                    <div class="image-container">
                        <loader v-if="imageLoading"></loader>
                        <div class="image-wrapper" :style="imageWrapperStyle">
                            <img :src="shareImageUrl" @load="onImageLoad" />
                        </div>
                    </div>
                    <div class="buttom-menu" :style="menuStyle">
                        <div class="handle"></div>
                        <div class="container">
                            <p class="heading" v-if="title">{{ title }}</p>
                            <div class="link-container">
                                <p
                                    v-if="instagramInstalled"
                                    :class="shareButtonClass"
                                >
                                    <instagram-icon />
                                    <a
                                        class="link"
                                        @click.stop.prevent="shareInstagram"
                                        >Share on Instagram</a
                                    >
                                </p>
                                <p :class="shareButtonClass">
                                    <facebook-icon />
                                    <a
                                        class="link"
                                        @click.stop.prevent="shareFacebook"
                                        >Share on Facebook</a
                                    >
                                </p>
                                <p :class="shareButtonClass">
                                    <sms-icon />
                                    <a
                                        class="link"
                                        @click.stop.prevent="shareSMS"
                                        >Share via SMS</a
                                    >
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </transition>
</template>

<script>
import $ from "webpack-zepto";
import { mapGetters } from "vuex";

import Loader from "../common/loader.vue";
import FacebookIcon from "../common/share/facebook-icon.vue";
import InstagramIcon from "../common/share/instagram-icon.vue";
import SmsIcon from "../common/share/sms-icon.vue";

export default {
    props: {
        title: String,
        type: {
            type: String,
            default: "outfit",
        },
        id: Number | String,
    },
    components: {
        Loader,
        InstagramIcon,
        FacebookIcon,
        SmsIcon,
    },
    data() {
        return {
            dragYstart: 0,
            lastY: 0,
            dragThreshold: 50,
            defaultMenuHeight: 170,
            dragDown: false,
            menuStyle: {
                bottom: "0px",
            },
            imageWrapperStyle: {
                opacity: 0,
            },
            instagramInstalled: false,
            imageLoading: true,
            currentIcon: null,
        };
    },
    computed: {
        isSharingDisabled() {
            return this.imageLoading || this.shareLoading;
        },
        shareButtonClass() {
            return {
                disabled: this.isSharingDisabled,
            };
        },
        ...mapGetters({
            shareImageUrl: "share/imageUrl",
            shareLoading: "share/getLoadingStatus",
        }),
    },
    methods: {
        dragstart(ev) {
            this.dragYstart = ev.touches[0].pageY;
        },
        dragend(ev) {
            if (
                this.dragDown &&
                ev.changedTouches[0].pageY - this.dragYstart >
                    this.dragThreshold
            ) {
                this.closePopup();
            } else if (
                !this.dragDown ||
                ev.changedTouches[0].pageY - this.dragYstart <=
                    this.dragThreshold
            ) {
                this.menuStyle.bottom = "0px";
                if (!this.imageLoading) this.imageWrapperStyle.opacity = 1;
            }
            this.dragYstart = 0;
        },
        dragmove(ev) {
            if (ev.changedTouches[0].pageY > this.dragYstart) {
                this.menuStyle.bottom =
                    this.dragYstart - ev.changedTouches[0].pageY + "px";
                this.imageWrapperStyle.opacity = Math.max(
                    0,
                    1 -
                        (ev.changedTouches[0].pageY - this.dragYstart) /
                            this.defaultMenuHeight
                );
            }
            this.dragDown = this.lastY < ev.changedTouches[0].pageY;
            this.lastY = ev.changedTouches[0].pageY;
        },
        closePopup() {
            this.menuStyle.bottom = "-" + this.defaultMenuHeight + "px";
            this.imageWrapperStyle.opacity = 0;

            this.$emit("close");
        },
        onImageLoad() {
            this.imageWrapperStyle.opacity = 1;
            this.imageLoading = false;
        },
        shareInstagram() {
            if (this.isSharingDisabled) return;
            this.currentIcon = InstagramIcon;
            return this.$store.dispatch("share/instagram");
        },
        shareFacebook() {
            if (this.isSharingDisabled) return;
            this.currentIcon = FacebookIcon;
            return this.$store.dispatch("share/facebook");
        },
        shareSMS() {
            if (this.isSharingDisabled) return;
            this.currentIcon = SmsIcon;
            return this.$store.dispatch("share/sms");
        },
    },
    created() {
        const { type, id } = this;
        this.$store.dispatch("share/setUrl", { type, id });

        if (typeof Instagram !== "undefined") {
            Instagram.isInstalled((err, installed) => {
                this.instagramInstalled = installed;
            });
        }
    },
};
</script>

<style lang="scss" scoped>
.share-container {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.71);

    &.visible {
        .buttom-menu {
            bottom: 0;
        }
    }
}

.image-container {
    box-sizing: border-box;
    bottom: 190px;
    top: 20px;
    left: 20px;
    right: 20px;
    position: absolute;
    max-height: 70vh;

    .image-wrapper {
        background: #fff;
        border-radius: 6px;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        width: 100%;
        height: auto;
        max-height: 70vh;
        position: absolute;
        overflow: hidden;
        transition: all 0.3s ease;

        img {
            max-width: 100%;
            max-height: 70vh;
            width: auto;
            height: auto;
            object-fit: contain;
            margin: 0 auto;
            display: block;
        }
    }
}

.buttom-menu {
    bottom: -170px;
    box-sizing: border-box;
    left: 0;
    position: absolute;
    right: 0;
    transition: bottom 0.3s ease;

    .handle {
        border: 3px solid rgba(255, 255, 255, 0.62);
        border-radius: 3px;
        width: 44px;
        height: 1px;
        position: relative;
        top: -20px;
        left: 50%;
        transform: translateX(-50%);
    }

    .container {
        background: #ffffff;
        border-radius: 18px 18px 0 0;
        padding: 20px;
        display: flex;
        flex-direction: column;
        text-align: left;
        min-height: 100px;

        @media (min-height: 737px) {
            padding: 25px 20px;
        }

        .heading {
            font-weight: 700;
            font-size: 21px;
            margin-bottom: 10px;
            @media (min-height: 737px) {
                margin-bottom: 22px;
            }
        }

        .link-container {
            display: flex;
            flex-direction: column;
            flex: 1;

            p {
                margin: 9px 0;

                .disabled {
                    color: #95989a;
                }

                @media (min-height: 737px) {
                    margin: 15px 0;
                }
            }

            .link {
                font-family: "Lato";
                font-size: 16px;
                letter-spacing: 0;
                line-height: 12px;
            }

            .icon {
                vertical-align: sub;
                margin-right: 12px;
            }
        }
    }
}

.share-loading {
    width: 90vw;
    height: 86vh;
    margin: 7vh auto 0;
    border: 1px solid #f4f4f4;
    border-radius: 5px;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    p {
        font-size: 18px;
        line-height: 21px;
        text-align: center;
        max-width: 70%;
    }
    img {
        max-width: 80%;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.19);
        border-radius: 10px;
    }
    svg {
        width: 30px;
        height: 30px;
    }
    .loading-indicator {
        display: flex;
        width: 30px;
        height: 20px;
        div {
            transform: scaleY(0);
            border-radius: 3px;
            background-color: #f2c0de;
            flex: 1;
            animation: scale 1s infinite ease-in;
            margin: 0 2.5px;
            &:nth-child(1) {
                animation-delay: 0.4s;
            }
            &:nth-child(2) {
                animation-delay: 0.6s;
            }
            &:nth-child(3) {
                animation-delay: 0.8s;
            }
        }
    }
}

@keyframes scale {
    0% {
        transform: scaleY(0);
    }
    50% {
        transform: scaleY(1);
    }
    100% {
        transform: scaleY(0);
    }
}
</style>
